import { isPositiveNumber } from '@opallabs/lambda'

/**
 * Safely truncate a string. Handles multibyte graphemes (characters) like
 * emoji, non-Latin scripts (including languages like Japanese that don’t use
 * whitespace to separate words), and composed Unicode graphemes like ‘é’
 * (‘´’ + ‘e’). Appends an ellipsis if the string was more than `maxLength`
 * graphemes long.
 *
 * @example truncateText("おはようございます。今日はいい天気ですね。", 4)
 * //=> "おはよう…"
 *
 * @note This will always trim trailing whitespace, even if `text` is less than
 * `maxLength` graphemes long.
 * @note If there’s exactly one non-whitespace grapheme beyond `maxLength` that
 * grapheme will be included rather than adding an ellipsis.
 */
export function truncateText(
  text: string,
  maxLength: number,
  { trimLeadingWhitespace = false } = {}
) {
  if (!(typeof text === 'string')) {
    throw new TypeError(`text must be a string: ${text}`)
  }
  if (!isPositiveNumber(maxLength)) {
    throw new RangeError(`maxLength must be a positive number: ${maxLength}`)
  }

  const segmenter = new Intl.Segmenter(navigator.languages, {
    granularity: 'grapheme',
  })
  const trimmed = trimLeadingWhitespace ? text.trim() : text.trimEnd()
  const segments = Array.from(segmenter.segment(trimmed))
  // Short-circuit if the string has <= maxLength graphemes
  if (segments.length <= maxLength) return trimmed

  // `.replace` consolidates a trailing run of whitespace to the first
  // whitespace grapheme in the run.
  const truncated = segments
    .slice(0, maxLength)
    .map(({ segment }) => segment)
    .join('')
    .replace(/(\p{White_Space})\p{White_Space}*$/u, '$1')

  const remainingGraphemes = segments.slice(maxLength)
  // @note `firstRemainingNonWhitespaceSegment` must exist: `trimmed` has
  // already removed trailing whitespace and short-circuited, so there must be
  // at least one non-whitespace grapheme following the graphemes in
  // `truncated`.
  let firstRemainingNonWhitespaceSegment!: Intl.SegmentData
  // `Intl.SegmentData` has an `index` property, but it's for bytes, not
  // graphemes
  let firstRemainingNonWhitespaceSegmentPosition!: number
  for (let index = 0; index < remainingGraphemes.length; index++) {
    const segment = remainingGraphemes[index]
    if (/\p{White_Space}/u.test(segment.segment)) continue

    firstRemainingNonWhitespaceSegment = segment
    firstRemainingNonWhitespaceSegmentPosition = index
    break
  }

  // If `firstRemainingNonWhitespaceSegment` has the final grapheme append it to
  // `truncated` and return, without adding an ellipsis. (The ellipsis would be
  // adding a character anyway.)
  if (
    firstRemainingNonWhitespaceSegmentPosition + 1 >=
    remainingGraphemes.length
  ) {
    return `${truncated}${firstRemainingNonWhitespaceSegment.segment}`
  }

  // If there's more than one non-whitespace segment trim the trailing
  // whitespace on `truncated` (if present) and append the ellipsis.
  //
  // @note Not all languages use '…', but there doesn't seem to be a way to get
  // a locale-specific ellipsis from the `Intl` API.
  // @see https://en.wikipedia.org/wiki/Ellipsis#In_different_languages
  return `${truncated.trimEnd()}…`
}
